import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Spin } from "antd";
import { WarningFilled } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useUser } from "../../contexts/UserContext/UserContext";
import { useToast } from "../../contexts/ToastContext";
import apiClient from "../../helpers/apiClient";
import {
    PAYMENT_METHOD_VALUES,
    PaymentMethodSelector,
    buildAdminPaymentOptions,
    buildCoverageAddedPath,
    buildCoveragePaymentUrls,
    createDepositPaymentCheckout,
    fetchReservationById,
    getDefaultPaymentMethod,
    registerReservationCashPayment,
    sendReservationPaymentLink,
} from "../reservations/lib/reservationPayment";
import { getCompanyId } from "../insurance/lib/insuranceSaleApi";
import { isSolaGateway, sendSolaPaymentLink } from "../../lib/sola/solaPaymentApi";
import useSolaCardPayment, { isSolaPaymentCancelled } from "../../components/payment/useSolaCardPayment";
import InsuranceSelectionSection from "./components/InsuranceSelectionSection";
import AddCoverageSummaryCard from "./components/AddCoverageSummaryCard";
import {
    getAlreadyPaidAmount,
    getReservationPatchIds,
} from "./utils/reservationHelpers";

const AddCoverageContent = ({ reservationId }) => {
    const { t } = useTranslation(["addCoverage", "reservationPayment"]);
    const history = useHistory();
    const { requestSolaCardPayment, solaCardModal } = useSolaCardPayment();
    const toast = useToast();
    const { myCompanyData: companyData } = useUser();

    const defaultPaymentGateway = companyData?.defaultPaymentGateway || "stripe";
    const showCashPayment = companyData?.preferences?.showCashPayment === true;

    const [reservation, setReservation] = useState(null);
    const [ready, setReady] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [selectedInsuranceId, setSelectedInsuranceId] = useState(null);
    const [paymentSystem, setPaymentSystem] = useState(null);
    const [insuranceState, setInsuranceState] = useState({
        insuranceAddonsData: [],
        selectedInsuranceKey: null,
        axleTokenData: null,
        isAxleVerifying: false,
        isAxleOptionSelected: false,
    });

    const paymentOptions = useMemo(
        () =>
            buildAdminPaymentOptions({
                defaultPaymentGateway,
                showCashPayment,
                t,
            }),
        [defaultPaymentGateway, showCashPayment, t],
    );

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                const next = await fetchReservationById(reservationId);
                if (!next) {
                    toast.error({ message: t("notFound") });
                    history.push(`/reservations/${reservationId}`);
                    return;
                }

                if (cancelled) return;
                setReservation(next);
                setReady(true);
            } catch {
                toast.error({ message: t("loadError") });
                history.push(`/reservations/${reservationId}`);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [history, toast, reservationId, t]);

    const showPaymentMethod = Boolean(
        selectedInsuranceId && !insuranceState.isAxleOptionSelected && !insuranceState.axleTokenData,
    );

    useEffect(() => {
        if (!showPaymentMethod) {
            setPaymentSystem(null);
            return;
        }
        if (!paymentSystem && paymentOptions.length > 0) {
            setPaymentSystem(getDefaultPaymentMethod(paymentOptions));
        }
    }, [paymentOptions, paymentSystem, showPaymentMethod]);

    const canContinue = useMemo(() => {
        if (insuranceState.isAxleVerifying) return false;
        if (insuranceState.axleTokenData) return true;
        if (insuranceState.isAxleOptionSelected) return false;
        if (selectedInsuranceId && selectedInsuranceId !== "own") {
            if (!paymentSystem) return false;
            return true;
        }
        return Boolean(selectedInsuranceId);
    }, [insuranceState, paymentSystem, selectedInsuranceId]);

    const handleContinue = async () => {
        if (!reservation || !canContinue) {
            toast.error({ message: t("selectInsuranceRequired") });
            return;
        }

        const selectedAddon = insuranceState.insuranceAddonsData.find((item) => item.id === selectedInsuranceId);

        try {
            setSubmitting(true);

            if (insuranceState.axleTokenData) {
                const { category, pickupLocation } = getReservationPatchIds(reservation);
                const payload = {
                    ...reservation,
                    category,
                    pickupLocation,
                    insuranceType: "axle",
                    addonsInsuranceId: null,
                    axleInsurance: {
                        accessToken: insuranceState.axleTokenData.accessToken,
                        policyId: insuranceState.axleTokenData.policies?.[0],
                    },
                };

                await apiClient.patch(`/api/reservation/${reservationId}`, payload);

                history.push(
                    buildCoverageAddedPath(reservationId, {
                        insuranceName: t("ownInsuranceName"),
                        amount: 0,
                    }),
                );
                return;
            }

            if (!selectedInsuranceId || selectedInsuranceId === "own") {
                toast.error({ message: t("selectInsuranceRequired") });
                return;
            }

            const provider = selectedAddon?.provider || "teqMobility";
            const selectedPayment = paymentSystem || defaultPaymentGateway || "stripe";

            if (!selectedPayment) {
                toast.error({ message: t("selectPaymentRequired") });
                return;
            }

            const { category, pickupLocation } = getReservationPatchIds(reservation);
            const carId = reservation?.carSelected?._id || reservation?.carSelected?.id;
            const existingInsuranceId = reservation?.addonsInsuranceId || "";
            const regularAddons = Array.isArray(reservation?.addons)
                ? reservation.addons.filter((id) => id && id !== existingInsuranceId)
                : [];

            const invoiceRes = await apiClient.post("/api/reservation/invoice-v2", {
                pickupDate: new Date(reservation?.exactPickupTime || reservation?.pickupDate),
                dropoffDate: new Date(reservation?.exactDropoffTime || reservation?.dropoffDate),
                category,
                location: pickupLocation,
                addonIdList: regularAddons,
                carId,
                insuranceId: selectedInsuranceId,
            });

            if (!invoiceRes.data?.success) {
                throw new Error(invoiceRes.data?.message || "Failed to generate insurance invoice");
            }

            const newInvoice = invoiceRes.data.data.invoice || {};
            const alreadyPaid = getAlreadyPaidAmount(reservation);
            const newTotal = Number(newInvoice.total || 0);
            const dueAmount = Math.max(0, Math.round((newTotal - alreadyPaid) * 100) / 100);

            const updatedInvoice = {
                ...newInvoice,
                deposit: alreadyPaid,
                due: dueAmount,
                paymentEvent: reservation?.invoice?.paymentEvent || newInvoice.paymentEvent || [],
            };

            const patchPayload = {
                ...reservation,
                category,
                pickupLocation,
                insuranceType: provider,
                addonsInsuranceId: selectedInsuranceId,
                addons: regularAddons,
                axleInsurance: null,
                invoice: updatedInvoice,
            };

            await apiClient.patch(`/api/reservation/${reservationId}`, patchPayload);

            const successInfo = {
                insuranceName: selectedAddon?.name || provider,
                amount: dueAmount,
            };

            if (dueAmount <= 0) {
                history.push(buildCoverageAddedPath(reservationId, successInfo));
                return;
            }

            const customerEmail = reservation?.email;
            if (!customerEmail) {
                throw new Error("Missing customer email for insurance payment");
            }

            const companyId = getCompanyId(companyData);

            if (selectedPayment === PAYMENT_METHOD_VALUES.CASH) {
                await registerReservationCashPayment(reservationId, dueAmount, "scheduled");
                history.push(buildCoverageAddedPath(reservationId, successInfo));
                return;
            }

            // Sola: the coverage and its invoice are already saved above, so only the difference
            // is charged. A card is charged on the spot; a link lands the customer on the booking
            // site's coverage-added screen, as a Stripe link does.
            if (isSolaGateway(companyData)) {
                if (selectedPayment === PAYMENT_METHOD_VALUES.SEND_LINK) {
                    await sendSolaPaymentLink({
                        reservationId,
                        reservationProcess: "scheduled",
                        amount: dueAmount,
                        customerEmail,
                        companyId,
                        successView: "coverageAdded",
                        insuranceName: successInfo.insuranceName,
                    });
                    toast.success({
                        message: t("reservationPayment:sendLink.successTitle"),
                        description: t("reservationPayment:sendLink.successDescription"),
                    });
                    history.push(`/reservations/${reservationId}`);
                    return;
                }

                try {
                    await requestSolaCardPayment({
                        reservationId,
                        reservationProcess: "scheduled",
                        amount: dueAmount,
                        customerEmail,
                        companyId,
                    });
                } catch (solaError) {
                    if (isSolaPaymentCancelled(solaError)) {
                        setSubmitting(false);
                        return;
                    }
                    throw solaError;
                }
                history.push(buildCoverageAddedPath(reservationId, successInfo));
                return;
            }

            const { successUrl, cancelUrl, paymentMetadata } = buildCoveragePaymentUrls({
                reservationId,
                dueAmount,
                insuranceName: selectedAddon?.name || provider,
                provider,
                selectedInsuranceId,
                reservation,
                companyId,
            });

            const checkout = await createDepositPaymentCheckout({
                reservationId,
                amount: dueAmount,
                customerEmail,
                successUrl,
                cancelUrl,
                metadata: paymentMetadata,
                companyId,
            });

            const paymentUrl = checkout?.paymentUrl;
            if (!paymentUrl) {
                throw new Error("Payment URL not received");
            }

            if (selectedPayment === PAYMENT_METHOD_VALUES.SEND_LINK) {
                await sendReservationPaymentLink({
                    reservationId,
                    customerEmail,
                    paymentUrl,
                    companyId,
                });
                toast.success({
                    message: t("reservationPayment:sendLink.successTitle"),
                    description: t("reservationPayment:sendLink.successDescription"),
                });
                history.push(`/reservations/${reservationId}`);
                return;
            }

            window.location.href = paymentUrl;
        } catch (error) {
            toast.error({
                message: t("purchaseError"),
                description: error?.response?.data?.message || error?.message,
            });
            setSubmitting(false);
        }
    };

    if (!ready) {
        return (
            <div className="flex min-h-[320px] items-center justify-center py-12">
                <Spin size="large" tip={t("loading")} />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3">
            {solaCardModal}
            <button
                type="button"
                onClick={() => history.push(`/reservations/${reservationId}`)}
                className="fontRegular w-fit text-sm text-[#7f8c8d] hover:text-[#5D5DF6]">
                ← {t("backToDetails")}
            </button>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_380px] xl:gap-8">
                <div className="min-w-0">
                    <Alert
                        type="error"
                        showIcon
                        icon={<WarningFilled className="!text-[#F04438]" />}
                        className="!mb-8 !rounded-2xl !border-[#FECDCA] !bg-[#FEF3F2] [&_.ant-alert-message]:!text-[#B42318] [&_.ant-alert-description]:!text-[#912018]"
                        message={<span className="fontSemi">{t("warningTitle")}</span>}
                        description={<span className="fontRegular text-sm">{t("warningBody")}</span>}
                    />

                    <div className="rounded-2xl border border-[#EEF0F6] bg-white p-5 shadow-[0_8px_30px_rgba(17,24,39,0.06)] md:p-8">
                        <InsuranceSelectionSection
                            reservation={reservation}
                            selectedInsuranceId={selectedInsuranceId}
                            onSelectedInsuranceIdChange={setSelectedInsuranceId}
                            onSelectionStateChange={setInsuranceState}
                            t={t}
                        />

                        {showPaymentMethod ? (
                            <PaymentMethodSelector
                                title={t("paymentTitle")}
                                subtitle={t("paymentSubtitle")}
                                options={paymentOptions}
                                value={paymentSystem}
                                onChange={setPaymentSystem}
                            />
                        ) : null}
                    </div>

                    <Button
                        type="primary"
                        size="large"
                        loading={submitting}
                        disabled={!canContinue || submitting}
                        onClick={handleContinue}
                        className="!mt-5 !h-12 !rounded-xl !px-8 !font-semibold lg:!mt-6">
                        {insuranceState.axleTokenData ? t("addCoverage") : t("continue")}
                    </Button>
                </div>

                <AddCoverageSummaryCard
                    reservation={reservation}
                    selectedInsuranceId={selectedInsuranceId}
                    t={t}
                />
            </div>
        </div>
    );
};

AddCoverageContent.propTypes = {
    reservationId: PropTypes.string.isRequired,
};

export default AddCoverageContent;
